import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMateDetail } from '../../../api';

const MateDetails = () => {
    const { mateId } = useParams();
    const navigate = useNavigate();
    const [mate, setMate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activePromptTab, setActivePromptTab] = useState('en');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchMateDetail = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getMateDetail(mateId);
                setMate(data);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch mate details.');
                console.error('Error fetching mate details:', err);
            } finally {
                setLoading(false);
            }
        };

        if (mateId) {
            fetchMateDetail();
        }
    }, [mateId, navigate]);

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ color: '#707F81', fontSize: '1.2rem' }}>Loading mate details...</div>
            </div>
        );
    }

    if (error || !mate) {
        return (
            <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '100px' }}>
                <div style={{ color: '#dc2626', background: '#fee2e2', padding: '16px 24px', borderRadius: '8px', marginBottom: '20px' }}>
                    {error || 'Mate not found'}
                </div>
                <button onClick={() => navigate('/mates-list')} className="btn btn-primary" style={{ padding: '10px 20px' }}>Go Back</button>
            </div>
        );
    }

    const availablePrompts = [
        { key: 'en', label: 'English', value: mate.agent_prompt_en },
        { key: 'ua', label: 'Ukrainian', value: mate.agent_prompt_ua },
        { key: 'jp', label: 'Japanese', value: mate.agent_prompt_jp },
        { key: 'es', label: 'Spanish', value: mate.agent_prompt_es },
        { key: 'de', label: 'German', value: mate.agent_prompt_de },
        { key: 'fr', label: 'French', value: mate.agent_prompt_fr },
        { key: 'default', label: 'Default', value: mate.agent_prompt }
    ].filter(prompt => prompt.value); // Only show tabs with content

    return (
        <div style={{ minHeight: '100vh', background: '#ccdff5', padding: '40px 20px' }}>
            <div style={{
                maxWidth: '1000px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header Actions */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <button
                        onClick={() => navigate('/mates-list')}
                        style={{
                            background: 'transparent',
                            border: '1px solid #cfd8dc',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#3c5a5d',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e5eff8' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                    >
                        ← Back to Mates List
                    </button>
                    <button
                        onClick={() => navigate(`/mate-edit/${mateId}`)}
                        style={{
                            background: '#3c5a5d',
                            border: 'none',
                            padding: '8px 24px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#fff',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#2c4346' }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#3c5a5d' }}
                    >
                        Edit Mate
                    </button>
                </div>

                {/* Profile Header section */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '30px',
                    marginBottom: '40px',
                    paddingBottom: '30px',
                    borderBottom: '1px solid #e2e8f0',
                    flexWrap: 'wrap'
                }}>
                    {mate.avatar_link && (
                        <div style={{
                            width: '150px',
                            height: '150px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '4px solid #fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            flexShrink: 0
                        }}>
                            <img
                                src={mate.avatar_link}
                                alt={mate.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}

                    <div style={{ flex: 1, minWidth: '300px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <h1 style={{ color: '#3c5a5d', margin: 0, fontSize: '2.5rem' }}>
                                {mate.name}
                            </h1>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {mate.is_premium && (
                                    <span style={{ background: '#fef08a', color: '#854d0e', padding: '6px 12px', borderRadius: '16px', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                        ★ Premium
                                    </span>
                                )}
                                <span style={{ background: mate.is_active ? '#dcfce7' : '#fef2f2', color: mate.is_active ? '#166534' : '#991b1b', padding: '6px 12px', borderRadius: '16px', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {mate.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                            {mate.mate_type && (
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '600', textTransform: 'capitalize' }}>
                                    Type: {mate.mate_type}
                                </span>
                            )}
                            {mate.language && (
                                <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                    LANG: {mate.language}
                                </span>
                            )}
                            {mate.voice && (
                                <span style={{ background: '#f3e8ff', color: '#6b21a8', padding: '6px 14px', borderRadius: '20px', fontSize: '0.95rem', fontWeight: '600', textTransform: 'capitalize' }}>
                                    Voice: {mate.voice}
                                </span>
                            )}
                        </div>

                        {mate.avatar_history && (
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#475569', fontSize: '0.9rem', textTransform: 'uppercase' }}>Avatar History</h4>
                                <p style={{ margin: 0, color: '#334155', lineHeight: '1.5' }}>{mate.avatar_history}</p>
                            </div>
                        )}

                        {mate.voice_instruction && (
                            <div style={{ background: '#fdf4ff', padding: '16px', borderRadius: '12px', border: '1px solid #f5d0fe' }}>
                                <h4 style={{ margin: '0 0 8px 0', color: '#701a75', fontSize: '0.9rem', textTransform: 'uppercase' }}>Voice Instruction</h4>
                                <p style={{ margin: 0, color: '#4a044e', lineHeight: '1.5' }}>{mate.voice_instruction}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Prompts Section */}
                <div>
                    <h2 style={{ color: '#3c5a5d', marginBottom: '20px', fontSize: '1.8rem' }}>Agent Prompts</h2>

                    {availablePrompts.length > 0 ? (
                        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            {/* Tabs */}
                            <div style={{ display: 'flex', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
                                {availablePrompts.map(prompt => (
                                    <button
                                        key={prompt.key}
                                        onClick={() => setActivePromptTab(prompt.key)}
                                        style={{
                                            padding: '16px 24px',
                                            background: activePromptTab === prompt.key ? '#fff' : 'transparent',
                                            border: 'none',
                                            borderBottom: activePromptTab === prompt.key ? '2px solid #3b82f6' : '2px solid transparent',
                                            color: activePromptTab === prompt.key ? '#1d4ed8' : '#64748b',
                                            fontWeight: activePromptTab === prompt.key ? 'bold' : 'normal',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {prompt.label}
                                    </button>
                                ))}
                            </div>

                            {/* Selected Tab Content */}
                            <div style={{ padding: '24px' }}>
                                {availablePrompts.map(prompt => (
                                    <div key={prompt.key} style={{ display: activePromptTab === prompt.key ? 'block' : 'none' }}>
                                        <pre style={{
                                            margin: 0,
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                            fontFamily: 'inherit',
                                            lineHeight: '1.6',
                                            color: '#334155',
                                            background: '#f8fafc',
                                            padding: '20px',
                                            borderRadius: '12px',
                                            border: '1px dashed #cbd5e1'
                                        }}>
                                            {prompt.value}
                                        </pre>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div style={{ color: '#64748b', fontStyle: 'italic' }}>No agent prompts found for this mate.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MateDetails;
